import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Clarity from "@microsoft/clarity";
import { PROJECT_ID, claritySetUser, clarityEvent } from "../utils/clarityTracker";

/**
 * Drop this inside <Provider> in appcontainer.jsx.
 * It initialises Clarity once, identifies the logged-in admin user,
 * and auto-tracks every toast.success / toast.error / toast.warning
 * across the entire app without touching any other file.
 */
export default function ClarityUserSync() {
  const user = useSelector((state) => state.user.user);
  const role = useSelector((state) => state.user.role);
  const initialized = useRef(false);

  // Initialise Clarity once + subscribe to ALL toasts automatically
  useEffect(() => {
    if (initialized.current || typeof window === "undefined") return;
    Clarity.init(PROJECT_ID);
    initialized.current = true;

    const unsubscribe = toast.onChange((payload) => {
      if (payload.status !== "added") return;
      const message =
        typeof payload.content === "string" ? payload.content : "notification";

      if (payload.type === "success") {
        clarityEvent("success", { message: message.slice(0, 128) });
      } else if (payload.type === "error") {
        clarityEvent("error", { message: message.slice(0, 128) });
      } else if (payload.type === "warning") {
        clarityEvent("warning", { message: message.slice(0, 128) });
      }
    });

    return () => unsubscribe();
  }, []);

  // Identify admin user whenever user state changes (login / refresh)
  useEffect(() => {
    if (!initialized.current || !user) return;

    const id = user._id != null ? String(user._id) : null;
    if (!id) return;

    const email = typeof user.email === "string" ? user.email : null;
    const name =
      typeof user.name === "string"
        ? user.name
        : typeof user.username === "string"
          ? user.username
          : null;

    try {
      Clarity.identify(
        id,
        undefined,
        window.location.pathname,
        email || name || id,
      );
      claritySetUser(user, role);
    } catch {
      // noop
    }
  }, [user, role]);

  return null;
}
